import { Add16, ChevronDown16, ChevronRight16 } from '@carbon/icons-react';
// import { Divider as AntdDivider } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { Space } from 'api/space';
import { ReactComponent as Folder16 } from 'assets/svgs/folder-filled.svg';
// import { ReactComponent as SpotlightSvg } from 'assets/svgs/spotlight.svg';
import {
  CpBody00Secondary,
  CpBody01Primary,
} from 'components/atoms/customer-portal/typography';
import { ALL_COLORS_WITHOUT_GRAY, Color } from 'components/colors';
import { useIsResponsive, useIsTablet } from 'hooks/use-media-query';
import { getRandomColor } from 'utils/get-random-color';
import { CpAccountSpacesProps, ProjectSpaceInAccount } from './types';

const SpaceDiv = styled.div<{
  active?: boolean;
  noHover?: boolean;
}>`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: start;
  padding: var(--cp-spacing-05);
  padding-left: var(--cp-spacing-06);
  ${(props) =>
    props?.noHover
      ? css``
      : css`
          &:hover {
            background: var(--cp-background-as-white-opacity-02);
          }
          .cp-very-light-background &:hover {
            background: var(--cp-background-as-black-opacity-02);
          }
        `};

  ${(props) =>
    props.active
      ? css`
          background: var(--cp-background-as-white-opacity);

          .cp-very-light-background & {
            background: var(--cp-background-as-black-opacity);
          }
        `
      : css``};

  @media (max-width: 480px) {
    padding-left: var(--cp-spacing-05);
  }
`;

const SpaceIcon = styled.div<{ color?: Color }>`
  min-width: 32px;
  /* min-height: 32px; */
  width: 32px;
  /* height: 32px; */
  margin-right: var(--cp-spacing-03);
  display: flex;
  align-items: center;
  justify-content: center;

  ${(props) =>
    props.color
      ? css`
          svg {
            fill: var(--cp-${props.color}-60);
            path {
              stroke: var(--cp-${props.color}-60);
            }
          }
        `
      : css``};
`;

const ProjectNameWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  padding: var(--cp-spacing-04);
  height: 48px;

  :hover {
    background: var(--cp-background-as-white-opacity);
  }

  .cp-very-light-background & {
    :hover {
      background: var(--cp-background-as-black-opacity);
    }
  }
`;

const ProjectNameAndChevronWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: start;
  width: calc(100% - 50px);
`;

const ProjectChevronWrapper = styled.div`
  height: 32px;
  width: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--border-radius-small);
  cursor: pointer;
`;

const ProjectName = styled(CpBody00Secondary)`
  /* ellipsis */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
`;

const AddInProjectButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 24px;
  width: 24px;
  border-radius: var(--border-radius-small);
  cursor: pointer;
  :hover {
    background: var(--cp-background-as-white-opacity);
  }

  .cp-very-light-background &:hover {
    background: var(--cp-background-as-black-opacity);
  }
`;

const SpaceName = styled(CpBody01Primary)`
  margin-bottom: 0;
  flex: 1 1 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// const SectionTitle = styled(CpBody00Secondary)`
//   margin-bottom: 0;
// `;

const Sidebar = styled.div`
  width: 300px;
  height: 100%;
  background-color: var(--cp-background-as-white-opacity);
  padding-bottom: var(--cp-spacing-06);
  padding-top: var(--cp-spacing-06);

  overflow-y: auto;

  @media (min-width: 769px) {
    border-right: 1px solid var(--cp-border-subtle);
  }

  @media (max-width: 480px) {
    border: 1px solid var(--cp-border-subtle);
  }

  @media (max-width: 768px) {
    width: 100%;
    border-radius: var(--border-radius-small);
  }
`;

// const Divider = styled(AntdDivider)`
//   margin: var(--cp-spacing-05) 0;
//   border-color: var(--cp-border-subtle-auto-adjust);
// `;

const ProjectWrapper = styled.div`
  border-bottom: 1px solid var(--cp-border-subtle-auto-adjust);
  width: 100%;
`;

const ProjectSpacesListSection: React.FC<{
  projectAndSpaces: ProjectSpaceInAccount;
  currentSpace?: Space;
  search: string;
  onSpaceClick: CpAccountSpacesProps['onSpaceClick'];
}> = ({ projectAndSpaces, onSpaceClick, currentSpace, search }) => {
  const isResponsive = useIsResponsive();
  const [isExpanded, setIsExpanded] = useState(
    !!currentSpace &&
      projectAndSpaces.spaces.some(
        (space) => space.spaceId === currentSpace.spaceId,
      ),
  );

  const onChevronClick = useCallback(() => {
    setIsExpanded((p) => !p);
  }, []);

  useEffect(() => {
    setIsExpanded(
      !!currentSpace &&
        projectAndSpaces.spaces.some(
          (space) => space.spaceId === currentSpace.spaceId,
        ),
    );
  }, [currentSpace, projectAndSpaces.spaces]);

  if (!projectAndSpaces || !projectAndSpaces?.spaces) {
    return <></>;
  }
  const { spaces } = projectAndSpaces;

  return (
    <ProjectWrapper>
      <ProjectNameWrapper>
        <ProjectNameAndChevronWrapper>
          <ProjectChevronWrapper onClick={onChevronClick}>
            {isExpanded ? <ChevronDown16 /> : <ChevronRight16 />}
          </ProjectChevronWrapper>
          <ProjectName>{projectAndSpaces.project.projectName}</ProjectName>
        </ProjectNameAndChevronWrapper>
        <AddInProjectButton>
          <Add16 />
        </AddInProjectButton>
      </ProjectNameWrapper>
      {!isExpanded ? (
        <></>
      ) : (
        spaces?.map((space) => {
          if (
            isResponsive &&
            !!search?.trim() &&
            !space.spaceName.toLowerCase().includes(search.toLowerCase())
          ) {
            return <></>;
          }

          return (
            <SpaceDiv
              active={currentSpace?.spaceId === space.spaceId}
              key={space.spaceId}
              onClick={() =>
                onSpaceClick({
                  projectId: projectAndSpaces.project.projectId,
                  spaceId: space.spaceId,
                })
              }
            >
              <SpaceIcon
                color={getRandomColor(space.spaceName, ALL_COLORS_WITHOUT_GRAY)}
              >
                <Folder16 />
              </SpaceIcon>
              <SpaceName>{space.spaceName}</SpaceName>
            </SpaceDiv>
          );
        })
      )}
      {isExpanded ? <SpaceDiv noHover /> : <></>}
    </ProjectWrapper>
  );
};

export const CpAccountSpacesSidePane: React.FC<
  Pick<
    CpAccountSpacesProps,
    'search' | 'currentSpace' | 'onSpaceClick' | 'data'
  >
> = ({ search, currentSpace, onSpaceClick, data }) => {
  const isResponsive = useIsTablet();

  const content = useMemo(
    () => (
      <>
        <Sidebar>
          {data?.spaces?.map((project) => {
            return (
              <ProjectSpacesListSection
                currentSpace={currentSpace}
                projectAndSpaces={project}
                onSpaceClick={onSpaceClick}
                search={search}
              />
            );
          })}
        </Sidebar>
      </>
    ),
    [currentSpace, data?.spaces, onSpaceClick, search],
  );

  if (isResponsive) {
    return content;
  }

  return <>{!search?.trim() ? content : <></>}</>;
};
